import {useCallback, useState} from 'react'
import {envVars} from '@/envVars'
import {linearIssueCreate} from '../services/linear.http'
import {useBugReporterStore} from '../store/useBugReporterStore'
import {BugReportFormValues, EnvironmentSnapshot} from '../types'

const buildEnvironmentTable = (snapshot: EnvironmentSnapshot): string => {
    const rows: Array<[string, string]> = [
        ['Reporter', snapshot.reporterName],
        ['Email', snapshot.reporterEmail],
        ['URL', snapshot.url],
        ['Browser', snapshot.browser],
        ['OS', snapshot.os],
        ['Screen Resolution', snapshot.screenResolution],
        ['Viewport', snapshot.viewportSize],
        ['App Version', snapshot.appVersion],
        ['Timestamp (UTC)', snapshot.timestamp]
    ]
    const header = '| Key | Value |\n|---|---|'
    const body = rows.map(([key, value]) => `| ${key} | ${escapePipes(value)} |`).join('\n')
    return `## Environment\n\n${header}\n${body}`
}

const escapePipes = (value: string) => value.replace(/\|/g, '\\|')

const buildIssueDescription = (values: BugReportFormValues, snapshot: EnvironmentSnapshot): string => {
    const sections = [values.description?.trim() || '', '---', buildEnvironmentTable(snapshot)].filter(Boolean)
    return sections.join('\n\n')
}

export const useBugReporter = () => {
    const isOpen = useBugReporterStore(state => state.isOpen)
    const storeOpen = useBugReporterStore(state => state.open)
    const storeClose = useBugReporterStore(state => state.close)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const open = useCallback(() => {
        setSubmitError(null)
        storeOpen()
    }, [storeOpen])

    const close = useCallback(() => {
        if (isSubmitting) return
        storeClose()
        setSubmitError(null)
    }, [isSubmitting, storeClose])

    const submit = useCallback(
        async (values: BugReportFormValues, snapshot: EnvironmentSnapshot) => {
            setIsSubmitting(true)
            setSubmitError(null)
            try {
                const description = buildIssueDescription(values, snapshot)
                const issue = await linearIssueCreate({
                    title: values.title,
                    description,
                    teamId: envVars.VITE_LINEAR_TEAM_ID,
                    projectId: envVars.VITE_LINEAR_PROJECT_ID,
                    assigneeId: envVars.VITE_LINEAR_ASSIGNEE_ID
                })

                storeClose()
                setIsSubmitting(false)
                return {issue, partialFailure: false}
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error'
                setSubmitError(`Failed to create issue: ${message}`)
                setIsSubmitting(false)
                return null
            }
        },
        [storeClose]
    )

    return {isOpen, open, close, submit, isSubmitting, submitError}
}
